import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { IoArrowBack, IoChevronForward } from "react-icons/io5";
import { TbLock, TbAlertTriangle, TbCloudCheck, TbLoader2, TbCloudX, TbTrash, TbDoorExit, TbFilePencil } from "react-icons/tb";
import { ConfirmPopup } from "src/components/Popup/ConfirmPopup";
import { useDraftAutosave } from "./useDraftAutosave";
import { activeRows } from "src/components/GoaLineEditor/goaLineDraft";
import { useAuth } from "src/context/AuthProvider";
import { useApi } from "src/hooks/useApi";
import { apiRequest, ApiError } from "src/services/apiClient";
import { isStandard } from "src/utils/roles";
import type { Patient, Recipient } from "src/types";
import { StepCheck, type CheckItem } from "./StepCheck";
import { StepPreview } from "./StepPreview";
import { formatEuro } from "./StepServices";
import { doctorName } from "./StepTreatment";
import { consentStates, deliveryGaps, patientName } from "./patientSearch";
import { CREATE_STEPS, type CreateStepKey } from "./createSteps";
import { useCreateWizard } from "./useCreateWizard";
import { CreateOverview } from "./CreateOverview";
import { CreateFooterBar } from "./CreateFooterBar";
import { StepPatient, patientSummary } from "./StepPatient";
import { StepTreatment, treatmentSummary } from "./StepTreatment";
import { StepServices, servicesSummary } from "./StepServices";
import { treatmentIssues } from "./treatmentRules";
import { checkServices } from "./servicesRules";
import { useGoaCatalog } from "src/hooks/useGoaCatalog";
import { emptyDraft, hasDraftContent, restoreDraft, todayIso, toManualInvoicePayload, type InvoiceDraftData } from "./invoiceDraft";
import { useInvoiceOwnerChoice } from "src/hooks/useInvoiceOwnerChoice";
import { sendPlan, type SendSettings } from "./sendPlan";
import { allFindings, useGoaDraftCheck } from "./goaCheck";
import { applySuggestion, isDiagnosisSuggestion, useInvoiceSuggestions, type Suggestion } from "./suggestions";
import "./invoiceCreate.scss";

type CreatedInvoice = {
    id: string;
    readableId: string;
    send?: { ok: true } | { ok: false; error: string; waitsForConsent?: boolean };
};

/** What the invoice page announces after creating — sent, waiting for consent, or why it wasn't sent. */
function createdNotice(created: CreatedInvoice, t: (key: string, opts?: Record<string, unknown>) => string) {
    const n = (key: string) => t(`invoiceCreate.created.${key}`, { number: created.readableId });
    const sent = created.send;
    if (!sent) return { variant: "success" as const, message: n("created") };
    if (sent.ok) return { variant: "success" as const, message: n("sent") };
    if (sent.waitsForConsent) return { variant: "success" as const, message: n("waitsForConsent") };
    const known = ["LEGAL_NOT_ACCEPTED", "NO_RECIPIENT_EMAIL", "POST_ONLY", "EMAIL_FAILED"];
    return { variant: "error" as const, message: `${n("notSent")} ${t(`invoiceCreate.created.reasons.${known.includes(sent.error) ? sent.error : "other"}`)}` };
}

/**
 * /invoices/new — create an invoice by hand, step by step (chapter 18, info/features).
 * Opened from "Rechnung erstellen" on the Invoices page and the Dashboard.
 */
export default function InvoiceCreate() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { user } = useAuth();
    const wizard = useCreateWizard();
    const headingRef = useRef<HTMLHeadingElement>(null);
    const firstRender = useRef(true);
    const [draft, setDraft] = useState<InvoiceDraftData>(emptyDraft);
    const updateDraft = (patch: Partial<InvoiceDraftData>) => setDraft((d) => ({ ...d, ...patch }));
    const [searchParams, setSearchParams] = useSearchParams();
    const resumeId = searchParams.get("draft");
    // A draft id in the URL is loaded once; the autosave's own new id is written there too, so a reload continues it.
    const [draftLoading, setDraftLoading] = useState(!!resumeId);
    const [draftMissing, setDraftMissing] = useState(false);
    const resumedFromUrl = useRef<string | null>(null);
    const checkResumedPatient = useRef(false);
    const [confirmDiscard, setConfirmDiscard] = useState(false);

    // Same rule as the upload button (Invoices.tsx canWrite) — matches requireNotStandard.
    const role = user?.doctor?.userRights;
    const canCreate = user?.doctor?.permEdit !== undefined ? user.doctor.permEdit : !isStandard(role);

    const { data: patientsData, loading: patientsLoading, refetch: refetchPatients } = useApi<{ patients: Patient[] }>(canCreate ? "/patients" : null);
    const patients = patientsData?.patients ?? [];
    const selectedPatient = patients.find((p) => p.id === draft.patientId) ?? null;

    const owner = useInvoiceOwnerChoice(draft.ownerPick, canCreate);
    const today = todayIso();
    const issues = treatmentIssues({ ownerId: owner.ownerId, treatmentDates: draft.treatmentDates, today, diagnoses: draft.diagnoses });

    const { catalogByNr } = useGoaCatalog(canCreate);
    const servicesCheck = checkServices(draft.services, draft.treatmentDates, catalogByNr);

    // GOÄ checker (story 15, Pro or add-on): runs from step 4 on. Its findings, and duplicate codes on
    // every plan, are each confirmed before creating.
    const hasGoaChecker = user?.praxis?.entitlements?.goaChecker ?? (user?.praxis?.businessPlan ?? 1) >= 3;
    const checkIndex = CREATE_STEPS.findIndex((s) => s.key === "check");
    const goaCheck = useGoaDraftCheck(draft.services, draft.treatmentDates, canCreate && hasGoaChecker && wizard.current >= checkIndex, { patientId: draft.patientId, doctorId: owner.ownerId });
    const [goaAcked, setGoaAcked] = useState<Set<string>>(() => new Set());
    const goaFindingsList = allFindings(draft.services, draft.treatmentDates, goaCheck, catalogByNr);
    const goaUnconfirmed = goaFindingsList.filter((f) => !goaAcked.has(f.key)).length;
    // Still checking blocks too, so nothing is created before the check had its say; a failed check doesn't.
    const goaBlocks = goaUnconfirmed > 0 || (hasGoaChecker && goaCheck.status === "loading");
    // Suggestions (story 16b, Plus and above): from step 2 on (related diagnoses there, the diagnoses' codes in
    // step 3, everything in step 4); taking one changes the lines, which re-runs both checks.
    const hasSuggestions = (user?.praxis?.businessPlan ?? 1) >= 2;
    const treatmentIndex = CREATE_STEPS.findIndex((s) => s.key === "treatment");
    const suggestions = useInvoiceSuggestions(
        draft.services, draft.treatmentDates, draft.diagnoses,
        owner.showPicker ? draft.ownerPick || undefined : undefined,
        canCreate && hasSuggestions && wizard.current >= treatmentIndex,
    );
    const [dismissedSuggestions, setDismissedSuggestions] = useState<Set<string>>(() => new Set());
    const takeSuggestion = (s: Suggestion) => updateDraft({ services: applySuggestion(draft.services, s, catalogByNr) });
    const suggestionProps = {
        dismissed: dismissedSuggestions,
        onApply: takeSuggestion,
        onDismiss: (id: string) => setDismissedSuggestions((cur) => new Set(cur).add(id)),
        codeName: (code: string) => catalogByNr.get(code)?.description,
    };
    // Step 3's warnings sit above the lines: with many lines they're out of view, so *Weiter* carries a sign.
    const [serviceWarnings, setServiceWarnings] = useState<Record<string, number>>({});
    const reportWarnings = useCallback(
        (day: string, count: number) => setServiceWarnings((cur) => (cur[day] === count ? cur : { ...cur, [day]: count })),
        [],
    );
    const warningCount = Object.values(serviceWarnings).reduce((a, b) => a + b, 0);
    const showWarnings = () => {
        const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
        document.querySelector(".ic-step .goa-line-editor__conflict-banner")?.scrollIntoView?.({ block: "center", behavior: reduceMotion ? "auto" : "smooth" });
    };
    const toggleGoaAck = (key: string) =>
        setGoaAcked((cur) => {
            const next = new Set(cur);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });

    // Who pays, with contact data and consent — one copy for step 1 (where consent can be requested
    // and the QR dialog re-reads it) and the check/preview steps.
    const recipientId = selectedPatient?.recipient?.id ?? null;
    const { data: recipientData, loading: recipientLoading, refetch: refetchRecipient } = useApi<Recipient>(recipientId ? `/recipients/${recipientId}` : null);
    // The hook keeps the last answer while a newly picked patient's recipient loads — never show that one.
    const recipient = recipientData && recipientData.id === recipientId ? recipientData : null;
    const { data: settings } = useApi<SendSettings>(canCreate ? "/settings" : null);
    const consentRequired = settings?.requireLegalAcceptance !== false;
    const send = sendPlan(recipient, !!selectedPatient?.postOnly, settings, selectedPatient?.id);
    const gaps = recipient ? deliveryGaps(recipient) : null;
    const noDeliveryWay = !!gaps && gaps.email && gaps.address;

    // What each step needs before "Weiter".
    const stepReady: Record<CreateStepKey, boolean> = {
        patient: !!recipientId,
        treatment: issues.length === 0,
        services: servicesCheck.ok,
        check: true,
        preview: true,
    };
    // Steps can be revisited out of order, so creating re-checks everything, not just the last step.
    const readyToCreate = stepReady.patient && stepReady.treatment && stepReady.services && !!recipient && !noDeliveryWay && !goaBlocks;
    // The PDF preview needs only what the server's preview checks (patient, treatment, services) — not
    // a delivery way, the loaded contact data or the GOÄ confirmations; those only hold up creating.
    const readyForPreview = stepReady.patient && stepReady.treatment && stepReady.services;

    const checkItems: CheckItem[] = buildCheckItems();
    function buildCheckItems(): CheckItem[] {
        const c = (key: string, opts?: Record<string, unknown>) => t(`invoiceCreate.check.${key}`, opts);
        const consent = recipient ? consentStates(recipient, selectedPatient?.id) : null;
        const consentMissing = consentRequired && !!consent && consent.digital !== "signed" && consent.post !== "signed";
        const goaChecking = hasGoaChecker && goaCheck.status !== "done";
        return [
            {
                key: "patient", stepIndex: 0, state: stepReady.patient ? "ok" : "error", title: c("patient"),
                detail: selectedPatient ? `${patientName(selectedPatient)} · ${c("paidBy", { name: recipient ? patientName(recipient) : "…" })}` : c("patientMissing"),
            },
            {
                key: "treatment", stepIndex: 1, state: stepReady.treatment ? "ok" : "error", title: c("treatment"),
                detail: [owner.owner ? doctorName(owner.owner) : null, t("invoiceCreate.treatment.summaryDays", { count: draft.treatmentDates.length }), t("invoiceCreate.treatment.summaryDiagnoses", { count: draft.diagnoses.length })].filter(Boolean).join(" · "),
            },
            {
                key: "services", stepIndex: 2, state: stepReady.services ? "ok" : "error", title: c("services"),
                detail: `${t("invoiceCreate.services.summaryLines", { count: servicesCheck.rowCount })} · ${formatEuro(servicesCheck.totals.total)}`,
            },
            {
                key: "delivery", stepIndex: 0, state: !gaps ? "warn" : noDeliveryWay ? "error" : gaps.email || gaps.address ? "warn" : "ok", title: c("delivery"),
                detail: !gaps ? c("deliveryLoading") : noDeliveryWay ? c("deliveryNone") : gaps.email ? c("deliveryPostOnly") : gaps.address ? c("deliveryEmailOnly") : c("deliveryBoth"),
            },
            ...(consentMissing ? [{ key: "consent", stepIndex: 0, state: "warn" as const, title: c("consent"), detail: c("consentMissing") }] : []),
            ...(hasGoaChecker || goaFindingsList.length > 0
                ? [{
                    key: "goa", stepIndex: 2, title: c(hasGoaChecker ? "goa" : "billingNotes"),
                    state: !goaChecking && goaFindingsList.length === 0 ? "ok" as const : "warn" as const,
                    detail: goaCheck.status === "error" ? c("goaFailed")
                        : goaChecking ? c("goaLoading")
                        : goaFindingsList.length === 0 ? c("goaOk")
                        : goaUnconfirmed > 0 ? c("goaFindings", { count: goaFindingsList.length })
                        : c("goaAllAcked", { count: goaFindingsList.length }),
                }]
                : []),
        ];
    }

    // ── Drafts (story 9) ──────────────────────────────────────────────────────────
    const autosave = useDraftAutosave({
        snapshot: {
            step: wizard.current,
            data: draft,
            summary: {
                patientName: selectedPatient ? patientName(selectedPatient) : "",
                total: servicesCheck.totals.total,
                // Lines with a code only — step 3's empty starter line isn't a service yet.
                lineCount: draft.treatmentDates.reduce((n, d) => n + activeRows(draft.services[d] ?? []).filter((r) => r.code.trim()).length, 0),
            },
        },
        shouldSave: canCreate && !draftLoading && hasDraftContent(draft),
        onCreated: (id) => setSearchParams({ draft: id }, { replace: true }),
    });

    useEffect(() => {
        if (!resumeId || !canCreate || resumedFromUrl.current === resumeId || resumeId === autosave.draftId) {
            setDraftLoading(false);
            return;
        }
        resumedFromUrl.current = resumeId;
        let cancelled = false;
        setDraftLoading(true);
        apiRequest<{ id: string; step: number; data: InvoiceDraftData; summary: { patientName: string; total: number; lineCount: number } }>(`/invoice-drafts/${resumeId}`)
            .then((saved) => {
                if (cancelled) return;
                const restored = restoreDraft(saved.data);
                setDraft(restored);
                wizard.restore(saved.step ?? 0);
                autosave.adopt(saved.id, { step: saved.step ?? 0, data: restored, summary: saved.summary });
                checkResumedPatient.current = true;
            })
            .catch(() => {
                if (cancelled) return;
                setDraftMissing(true);
                setSearchParams({}, { replace: true });
            })
            .finally(() => !cancelled && setDraftLoading(false));
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resumeId, canCreate]);

    // A resumed draft's patient may have been deleted since — start step 1 over instead of waiting for it.
    useEffect(() => {
        if (!checkResumedPatient.current || !patientsData) return;
        checkResumedPatient.current = false;
        if (draft.patientId && !patientsData.patients.some((p) => p.id === draft.patientId)) updateDraft({ patientId: null });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [patientsData, draft.patientId]);

    const discardDraft = async () => {
        setConfirmDiscard(false);
        const id = autosave.draftId;
        autosave.stop();
        if (id) {
            try {
                await apiRequest(`/invoice-drafts/${id}`, { method: "DELETE" });
            } catch {
                /* already gone — starting over either way */
            }
        }
        const fresh = emptyDraft();
        setDraft(fresh);
        wizard.restore(0);
        autosave.reset({ step: 0, data: fresh, summary: { patientName: "", total: 0, lineCount: 0 } });
        setSearchParams({}, { replace: true });
    };

    // Leaving keeps the draft: a change still waiting for its save is saved on unmount. Only a draft
    // whose last save failed asks first — those changes would be lost.
    const [confirmExit, setConfirmExit] = useState(false);
    const exit = () => {
        if (autosave.status === "error") setConfirmExit(true);
        else navigate("/invoices");
    };

    const [creating, setCreating] = useState<false | "create" | "send">(false);
    const [createError, setCreateError] = useState<string | null>(null);
    /** `andSend` (story 12): the server sends the portal link right after creating; whatever it
     *  answers, the invoice exists — the invoice page then says whether it went out. */
    const createInvoice = async (andSend = false) => {
        if (!readyToCreate || creating) return;
        setCreating(andSend ? "send" : "create");
        setCreateError(null);
        // The server deletes the draft with the invoice — nothing may save it again after this, and a
        // save still in flight is awaited so its draft id goes along (no orphaned draft).
        autosave.stop();
        const draftId = await autosave.settle();
        try {
            const created = await apiRequest<CreatedInvoice>(
                "/invoices/manual",
                { method: "POST", body: JSON.stringify({ ...toManualInvoicePayload(draft, owner.showPicker, draftId), ...(andSend ? { send: true } : {}) }) },
                { "Content-Type": "application/json" },
            );
            navigate(`/invoices/${created.id}`, { replace: true, state: { createdNotice: createdNotice(created, t) } });
        } catch (e) {
            autosave.resume();
            const code = e instanceof ApiError ? e.body?.error : undefined;
            const known = ["TRIAL_INVOICE_LIMIT", "OWNER_REQUIRED", "PATIENT_NOT_FOUND", "NO_RECIPIENT", "INVALID_SERVICES", "NO_SERVICES", "NO_DIAGNOSIS"];
            setCreateError(t(`invoiceCreate.createErrors.${known.includes(code) ? code : "generic"}`, { limit: e instanceof ApiError ? e.body?.limit : undefined }));
            setCreating(false);
        }
    };

    // Moving between steps: scroll the new step's heading to the top (the overview above stays one
    // scroll away — on a laptop screen it would otherwise push the step's work area below the
    // fold) and put focus there, so keyboard and screen-reader users land where the content changed.
    useEffect(() => {
        if (firstRender.current) {
            firstRender.current = false;
            return;
        }
        const heading = headingRef.current;
        if (!heading) return;
        const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
        heading.scrollIntoView?.({ block: "start", behavior: reduceMotion ? "auto" : "smooth" });
        heading.focus({ preventScroll: true });
    }, [wizard.current]);

    const step = CREATE_STEPS[wizard.current];

    return (
        <div className="page invoice-create">
            <div className="detail-breadcrumb invoice-create__breadcrumb">
                <button type="button" className="btn-back" onClick={() => navigate(-1)}>
                    <IoArrowBack /> {t("common.back")}
                </button>
                <button type="button" className="detail-breadcrumb__parent detail-breadcrumb__action" onClick={() => navigate("/invoices")}>
                    {t("invoices.title")}
                </button>
                <IoChevronForward className="detail-breadcrumb__sep" />
                <span className="detail-breadcrumb__current">{t("invoiceCreate.title")}</span>
            </div>

            <div className="invoice-create__head">
                <h1 className="invoice-create__title">{t("invoiceCreate.title")}</h1>
                {canCreate && !draftLoading && (
                    <div className="invoice-create__draft">
                        <span className={`ic-save ic-save--${autosave.status}`} role="status" aria-live="polite">
                            {autosave.status === "saving" && <><TbLoader2 aria-hidden /> {t("invoiceCreate.draft.saving")}</>}
                            {autosave.status === "saved" && (
                                <><TbCloudCheck aria-hidden /> {autosave.savedAt
                                    ? t("invoiceCreate.draft.savedAt", { time: autosave.savedAt.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" }) })
                                    : t("invoiceCreate.draft.saved")}</>
                            )}
                            {autosave.status === "error" && (
                                <>
                                    <TbCloudX aria-hidden /> {t("invoiceCreate.draft.error")}
                                    <button type="button" className="ic-save__retry" onClick={() => void autosave.retry()}>
                                        {t("invoiceCreate.draft.retry")}
                                    </button>
                                </>
                            )}
                            {autosave.status === "idle" && t("invoiceCreate.draft.idle")}
                        </span>
                        {autosave.draftId && (
                            <button type="button" className="ic-discard" onClick={() => setConfirmDiscard(true)}>
                                <TbTrash aria-hidden /> {t("invoiceCreate.draft.discard")}
                            </button>
                        )}
                        <button type="button" className="ic-exit" onClick={exit}>
                            <TbDoorExit aria-hidden /> {t("invoiceCreate.exit.button")}
                        </button>
                    </div>
                )}
            </div>

            {canCreate && !draftLoading && (
                <div className="ic-note ic-note--draft ic-draft-info">
                    <TbFilePencil aria-hidden />
                    <span>{t("invoiceCreate.draft.info")}</span>
                </div>
            )}

            {confirmExit && (
                <ConfirmPopup
                    title={t("invoiceCreate.exit.unsavedTitle")}
                    message={t("invoiceCreate.exit.unsavedMessage")}
                    confirmLabel={t("invoiceCreate.exit.button")}
                    onConfirm={() => navigate("/invoices")}
                    onCancel={() => setConfirmExit(false)}
                />
            )}

            {confirmDiscard && (
                <ConfirmPopup
                    title={t("invoiceCreate.draft.discardTitle")}
                    message={t("invoiceCreate.draft.discardMessage")}
                    confirmLabel={t("invoiceCreate.draft.discard")}
                    onConfirm={() => void discardDraft()}
                    onCancel={() => setConfirmDiscard(false)}
                />
            )}

            {draftMissing && (
                <div className="ic-note ic-note--warn ic-draft-missing" role="status">
                    <TbAlertTriangle aria-hidden />
                    <span>{t("invoiceCreate.draft.missing")}</span>
                </div>
            )}

            {!canCreate ? (
                <div className="invoice-create__denied" role="alert">
                    <TbLock aria-hidden />
                    <p>{t("invoiceCreate.noPermission")}</p>
                </div>
            ) : draftLoading ? (
                <div className="ic-draft-loading" role="status">
                    <TbLoader2 aria-hidden /> {t("invoiceCreate.draft.loading")}
                </div>
            ) : (
                <>
                    <CreateOverview
                        wizard={wizard}
                        summaries={{
                            patient: patientSummary(selectedPatient, t),
                            treatment: treatmentSummary(draft, owner.owner, t),
                            services: servicesSummary(servicesCheck, t),
                        }}
                    />

                    <section className="ic-step" key={step.key} aria-labelledby="ic-step-heading">
                        <h2 id="ic-step-heading" className="ic-step__heading" ref={headingRef} tabIndex={-1}>
                            {t(`invoiceCreate.stepHeadings.${step.key}`)}
                        </h2>
                        <p className="ic-step__lead">{t(`invoiceCreate.stepLeads.${step.key}`)}</p>
                        {step.key === "patient" ? (
                            <StepPatient
                                patientId={draft.patientId}
                                onSelect={(patientId) => updateDraft({ patientId })}
                                patients={patients}
                                patientsLoading={patientsLoading}
                                onPatientsChanged={refetchPatients}
                                recipient={recipient}
                                recipientLoading={recipientLoading}
                                onRecipientChanged={refetchRecipient}
                                consentRequired={consentRequired}
                            />
                        ) : step.key === "treatment" ? (
                            <StepTreatment
                                draft={draft}
                                update={updateDraft}
                                owner={owner}
                                today={today}
                                issues={issues}
                                related={suggestions.status === "done" ? suggestions.relatedDiagnoses : []}
                            />
                        ) : step.key === "services" ? (
                            <StepServices
                                days={draft.treatmentDates}
                                services={draft.services}
                                onChange={(services) => updateDraft({ services })}
                                check={servicesCheck}
                                onWarnings={reportWarnings}
                                suggestions={hasSuggestions
                                    ? {
                                        ...suggestionProps,
                                        result: suggestions.status === "done"
                                            ? { ...suggestions, suggestions: suggestions.suggestions.filter(isDiagnosisSuggestion) }
                                            : suggestions,
                                    }
                                    : undefined}
                            />
                        ) : step.key === "check" ? (
                            <StepCheck
                                items={checkItems}
                                onOpenStep={wizard.goTo}
                                goa={{ findings: goaFindingsList, acked: goaAcked, onToggle: toggleGoaAck, upgrade: !hasGoaChecker }}
                                suggestions={hasSuggestions ? { ...suggestionProps, result: suggestions } : "upgrade"}
                            />
                        ) : (
                            <StepPreview
                                patient={selectedPatient}
                                recipient={recipient}
                                owner={owner.owner}
                                days={draft.treatmentDates}
                                services={draft.services}
                                diagnoses={draft.diagnoses}
                                totals={servicesCheck.totals}
                                pdfBody={readyForPreview ? toManualInvoicePayload(draft, owner.showPicker) : null}
                                send={send}
                            />
                        )}
                    </section>

                    {step.key === "preview" && goaUnconfirmed > 0 && (
                        <div className="ic-note ic-note--warn ic-goa-pending" role="status">
                            <TbAlertTriangle aria-hidden />
                            <span>{t(`invoiceCreate.check.${hasGoaChecker ? "goaConfirmFirst" : "billingNotesConfirmFirst"}`, { count: goaUnconfirmed })}</span>
                            <button type="button" onClick={() => wizard.goTo(checkIndex)}>{t("invoiceCreate.check.goaToCheck")}</button>
                        </div>
                    )}

                    {createError && (
                        <div className="ic-note ic-note--error ic-create-error" role="alert">
                            <TbAlertTriangle aria-hidden />
                            <span>{createError}</span>
                        </div>
                    )}

                    <CreateFooterBar
                        wizard={wizard}
                        total={servicesCheck.totals.total}
                        canContinue={stepReady[step.key]}
                        canCreate={readyToCreate && !creating}
                        creating={creating}
                        onCreate={() => void createInvoice()}
                        sendLabel={send?.kind === "send" ? t("invoiceCreate.createAndSend") : send?.kind === "afterConsent" ? t("invoiceCreate.createSendAfterConsent") : undefined}
                        onCreateAndSend={() => void createInvoice(true)}
                        warningCount={step.key === "services" ? warningCount : 0}
                        onShowWarnings={showWarnings}
                    />
                </>
            )}
        </div>
    );
}
